import { useCallback } from 'react';

import {
  CommentRepliesDocument,
  CommentRepliesQuery,
  CommentReplyFragment,
  getError,
  getTypeOrNull,
  isError,
  useCommentRepliesQuery,
  useCommentReplyCreatedSubscription,
} from '@spotify/confidence-graphql';

interface UseCommentReplies {
  commentReplies: CommentReplyFragment[];
  error?: Error;
  loadMore: () => void;
  hasMore: boolean;
}

export function useCommentReplies(comment: string): UseCommentReplies {
  const { data, error, fetchMore } = useCommentRepliesQuery({
    variables: {
      parent: comment,
      pageSize: 5,
    },
    onCompleted: result => {
      if (isError(result?.commentReplies)) {
        throw getError(result?.commentReplies);
      }
    },
  });

  const response = getTypeOrNull(data?.commentReplies);

  const commentReplies = response?.commentReplies ?? [];
  const nextPageToken = response?.nextPageToken;

  const loadMore = useCallback(() => {
    if (nextPageToken) {
      fetchMore({
        variables: {
          parent: comment,
          pageToken: nextPageToken,
        },
      });
    }
  }, [nextPageToken, fetchMore, comment]);

  const hasMore = !!nextPageToken;

  useCommentReplyCreatedSubscription({
    onData({ data: { data: event }, client }) {
      const commentReply = event?.commentReplyCreated?.reply;
      if (!commentReply) return;

      // The name of the reply contains the parent. The name has the
      // follow format: 'comments/{commentId}/replies/{replyId}'. We
      // want the `comments/{commentId}` part.
      const commentName = commentReply.name.split('/replies')[0];

      client.cache.updateQuery<CommentRepliesQuery>(
        {
          query: CommentRepliesDocument,
          variables: {
            parent: commentName,
            pageSize: 5,
          },
        },
        cachedData => updateCommentRepliesQuery(cachedData, commentReply),
      );

      /*
      client.cache.modify({
        fields: {
          commentReplies(
            existing = { commentReplies: [] },
            { storeFieldName, readField },
          ) {
            if (!storeFieldName.includes(commentName)) return existing;

            // check if the reply exists in the list already
            if (
              existing.commentReplies.find(
                c => readField('name', c) === commentReply.name,
              )
            ) {
              return existing;
            }

            return {
              ...existing,
              commentReplies: [commentReply, ...existing.commentReplies],
            };
          },
        },
      });
      */
    },
  });

  return { commentReplies, error, loadMore, hasMore };
}

export function updateCommentRepliesQuery(
  data: CommentRepliesQuery | null,
  commentReply: CommentReplyFragment,
): CommentRepliesQuery | null | undefined {
  // If we do not have any data it means that we're creating the first
  // reply.
  if (!data) {
    return {
      commentReplies: {
        __typename: 'CollaborationV1ListCommentRepliesResponse',
        commentReplies: [commentReply],
        nextPageToken: null,
      },
    };
  }

  if (!data) return undefined;

  // XXX: There is a race in the backend when comments are created and
  // we fetch the replies, in that we might not have permissions to
  // read the reply just milliseconds after it was created. The
  // comment replies query will have an error that says that we
  // cannot read.  The easiest way forward is to work around this in
  // the UI right now. If we see a "can_read" error, we just assume
  // there should be an empty list.
  let commentReplies: CommentReplyFragment[] = [];

  if (isError(data?.commentReplies)) {
    const error = getError(data.commentReplies);
    if (
      error?.message.startsWith(
        "Permission denied: missing relation 'can_read'",
      )
    ) {
      // We're OK with this error, fall through.
    } else {
      // We have a different error, let's throw it.
      return undefined;
    }
  } else {
    commentReplies = data?.commentReplies?.commentReplies ?? [];
  }

  // Check if the comment reply is already in the cache
  if (
    commentReplies.some(
      (reply: CommentReplyFragment) => reply.name === commentReply?.name,
    )
  ) {
    return undefined;
  }

  return {
    ...data,
    commentReplies: {
      nextPageToken: null,
      ...data?.commentReplies,
      __typename: 'CollaborationV1ListCommentRepliesResponse',
      commentReplies: [commentReply, ...commentReplies],
    },
  };
}
